import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/projectData/slice'
import { IUserAudio } from 'store/types/audio'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { generateAndGetAudioDefaultDataForProject } from 'utils/audio'
import { IAudioItem } from 'types/audio'
import { editorSelectors } from 'store/features/editor/selectors'
import { getUserSoundsByIds_API, uploadFile_API } from 'api'
import { isBlobUrl, scrollToElementById } from 'utils'
import {
  createDuplicatedItem,
  getNumericUserSoundIds,
  updateAudioWithVersion
} from 'store/features/projectData/utils'
import { withAppThunks } from 'store/with-app-thunks'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'

const { actions } = slice

const uploadMultilineAudios = (audios: { id: string; file: File }[]) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const formData = new FormData()
    audios.forEach(audio => {
      formData.append('file', audio.file)
    })

    const { audios: uploadedAudios } = await uploadFile_API(formData)

    if (!uploadedAudios) {
      return
    }

    const uploadedAudiosData = audios.map((audio, index) => ({
      id: audio.id,
      mediaPath: uploadedAudios[index].path,
      mediaId: uploadedAudios[index].id
    }))

    dispatch(appThunks.audioThunks.unshiftUserSound(uploadedAudios))
    dispatch(actions.updateUploadedAudioFilePaths(uploadedAudiosData))
  })

const addUserAudioToProject = (userAudio: IUserAudio, currentTime: number) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const nextAvailableLineIndex =
        projectDataSelectors.nextAvailableLineIndex(state)
      const newAudioData = generateAndGetAudioDefaultDataForProject({
        assetId: userAudio.id.toString(),
        duration: userAudio.duration,
        path: userAudio.path,
        title: userAudio.title,
        type: PROJECT_AUDIO_TYPES.SOUND,
        projectStart: currentTime,
        lineIndex: nextAvailableLineIndex
      })

      dispatch(
        actions.addMultiLineAudios({
          value: [newAudioData]
        })
      )

      // Set the newly added audio as active
      dispatch(
        appThunks.editorThunks.setActiveTimelineItems([
          {
            id: newAudioData.id,
            type: newAudioData.type
          }
        ])
      )

      // Scroll to the newly added audio
      scrollToElementById(newAudioData.id)
    }
  )

const duplicateAudioItem =
  (id: string) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const multilineAudios = projectDataSelectors.multilineAudios(state)
    const audioItem = multilineAudios.find(item => item.id === id)

    if (!audioItem || isBlobUrl(audioItem.mediaPath)) {
      return
    }

    const duplicatedItem = createDuplicatedItem<IAudioItem>(
      multilineAudios,
      id,
      'lineIndex',
      'projectStart'
    )
    if (duplicatedItem) {
      dispatch(actions.addMultiLineAudios({ value: [duplicatedItem] }))
    }

    return duplicatedItem?.id
  }
const removeSelectedAudio = () =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const audioId = editorSelectors.activeTimelineItemId(getState())
    if (!audioId) {
      return
    }
    dispatch(actions.removeAudiosFromProject([audioId]))
    dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
  })

const updateActiveAudioVolume =
  (volume: number) => (dispatch: TAppDispatch, getState: TGetState) => {
    const audioId = editorSelectors.activeTimelineItemId(getState())
    if (!audioId) {
      return
    }
    dispatch(
      actions.updateActiveAudioVolume({ volume, activeAudioId: audioId })
    )
  }

const updateSoundVersion =
  (version: IUserAudio) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const audioId = editorSelectors.activeTimelineItemId(state)

    if (!audioId) {
      return
    }

    const audio = projectDataSelectors
      .multilineAudios(state)
      .find(item => item.id === audioId)
    if (!audio) {
      return
    }
    const playbackSpeed = audio.playbackSpeed ?? 1

    const newVersion: IAudioItem = {
      ...audio
    }
    updateAudioWithVersion(newVersion, version, playbackSpeed)

    dispatch(actions.updateAudioItemData(newVersion))
  }

const getTimelineSoundsMetaData =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const soundTypeAudios = projectDataSelectors.soundTypeAudios(state)
    const soundIds = getNumericUserSoundIds(soundTypeAudios)
    if (!soundIds?.length) return

    try {
      const { data } = await getUserSoundsByIds_API({ ids: soundIds })
      dispatch(actions.setTimelineUserSoundsMetaData(data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
    }
  }

const getSoundMetaDataById =
  (id: string | number) => async (dispatch: TAppDispatch) => {
    const numericId = Number(id)
    try {
      const { data } = await getUserSoundsByIds_API({ ids: [numericId] })
      dispatch(actions.setTimelineUserSoundsMetaData(data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
    }
  }

export const projectDataAudioThunks = {
  uploadMultilineAudios,
  addUserAudioToProject,
  duplicateAudioItem,
  removeSelectedAudio,
  updateActiveAudioVolume,
  updateSoundVersion,
  getTimelineSoundsMetaData,
  getSoundMetaDataById
}
